// Customer order history — the feed of completed orders (confirmed, cancelled,
// refunded, disputed), with the official receipt downloadable for confirmed
// deliveries. In-flight orders live on the Requests page.

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Loader2, History as HistoryIcon, Download, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { PortalShell } from '../../components/shared/PortalShell';
import { Card, EmptyState, StatusPill } from '../../components/shared/primitives';
import { CUSTOMER_NAV } from '../_navs';
import { requests, receipts, type RequestStatus, type ServiceRequestWithItems, type CertLang } from '../../lib/api';
import { formatHtgMinor, formatUsdFromHtgMinor } from '../../lib/money';

const TERMINAL: RequestStatus[] = ['confirmed', 'cancelled', 'refunded', 'disputed'];

export function CustomerHistoryPage() {
  const q = useQuery({ queryKey: ['me', 'requests'], queryFn: () => requests.list() });

  const rows = useMemo(() =>
    (q.data ?? [])
      .filter((r) => TERMINAL.includes(r.status))
      .sort((a, b) => (b.updated_at ?? '').localeCompare(a.updated_at ?? '')),
    [q.data]);

  const confirmed = useMemo(() => rows.filter((r) => r.status === 'confirmed'), [rows]);
  const spent = useMemo(() => confirmed.reduce((a, r) => a + (r.cart_subtotal_htg_minor || 0), 0), [confirmed]);

  return (
    <PortalShell nav={CUSTOMER_NAV} activeId="history" title="History" subtitle="Completed orders, with downloadable delivery receipts.">
      <div className="space-y-4">
        {/* Summary */}
        <div className="grid grid-cols-3 gap-3">
          <Stat label="Delivered & received" value={String(confirmed.length)} tone="emerald" />
          <Stat label="Closed orders" value={String(rows.length)} />
          <Stat label="Total received value" value={spent > 0 ? formatHtgMinor(spent) : '—'} sub={spent > 0 ? `≈ ${formatUsdFromHtgMinor(spent)}` : undefined} />
        </div>

        <Card padding="p-0">
          {q.isLoading ? (
            <div className="text-center py-12 text-slate-400 text-[13px]"><Loader2 className="inline w-4 h-4 animate-spin" /> Loading…</div>
          ) : rows.length === 0 ? (
            <EmptyState
              icon={HistoryIcon}
              title="No completed orders yet"
              body="Once a delivery is confirmed (or an order is cancelled), it lands here with its receipt."
              action={<Link to="/customer/requests" className="inline-flex items-center gap-1 text-[13px] font-semibold text-brand-600 hover:underline">See active requests →</Link>}
            />
          ) : (
            <>
              <div className="sm:hidden divide-y divide-slate-100">
                {rows.map((r) => <HistoryCard key={r.id} r={r} />)}
              </div>
              <table className="hidden sm:table text-[12.5px] w-full">
                <thead className="text-[10.5px] uppercase tracking-wider text-slate-400 bg-slate-50">
                  <tr>
                    <th className="text-left py-2.5 px-4 font-medium">Order</th>
                    <th className="text-left py-2.5 px-3 font-medium">For</th>
                    <th className="text-left py-2.5 px-3 font-medium">Completed</th>
                    <th className="text-left py-2.5 px-3 font-medium">Status</th>
                    <th className="text-right py-2.5 px-3 font-medium">HTG</th>
                    <th className="text-right py-2.5 px-4 font-medium">Receipt</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {rows.map((r) => <HistoryRow key={r.id} r={r} />)}
                </tbody>
              </table>
            </>
          )}
        </Card>
      </div>
    </PortalShell>
  );
}

function Stat({ label, value, sub, tone }: { label: string; value: string; sub?: string; tone?: 'emerald' }) {
  return (
    <Card padding="p-3">
      <p className="text-[10.5px] uppercase tracking-wider font-semibold text-slate-400">{label}</p>
      <p className={`mt-1 text-[18px] font-bold ${tone === 'emerald' ? 'text-emerald-600' : 'text-slate-900'}`}>{value}</p>
      {sub && <p className="text-[11px] text-slate-400">{sub}</p>}
    </Card>
  );
}

function fmtDate(s?: string) {
  return s ? new Date(s).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '—';
}

function HistoryCard({ r }: { r: ServiceRequestWithItems }) {
  return (
    <div className="px-4 py-3">
      <div className="flex items-center justify-between gap-2">
        <Link to={`/customer/requests/${r.id}`} className="font-mono text-[12px] text-cyan-700">REQ-{String(r.id).padStart(4, '0')}</Link>
        <StatusPill status={r.status} />
      </div>
      <p className="mt-1 text-[13.5px] font-semibold text-slate-900 truncate">{r.title || r.category?.label || `Cat ${r.category_id}`}</p>
      <div className="mt-1 flex items-center justify-between text-[12px] text-slate-500">
        <span>For {r.recipient?.full_name?.split(' ')[0] ?? '—'} · {fmtDate(r.updated_at)}</span>
        <span className="font-mono text-slate-900">{r.cart_subtotal_htg_minor > 0 ? formatHtgMinor(r.cart_subtotal_htg_minor) : '—'}</span>
      </div>
      {r.status === 'confirmed' && (
        <div className="mt-2 flex items-center gap-3">
          <Link to={`/customer/requests/new?reorder=${r.id}`} className="text-[11.5px] font-semibold text-brand-600 hover:underline inline-flex items-center gap-1"><RotateCcw className="w-3 h-3" /> Order again</Link>
          <ReceiptLink requestId={r.id} />
        </div>
      )}
    </div>
  );
}

function HistoryRow({ r }: { r: ServiceRequestWithItems }) {
  return (
    <tr className="hover:bg-slate-50">
      <td className="py-2.5 px-4">
        <Link to={`/customer/requests/${r.id}`} className="block">
          <span className="font-mono text-cyan-700">REQ-{String(r.id).padStart(4, '0')}</span>
          <span className="text-slate-600"> · {r.category?.label ?? `Cat ${r.category_id}`}</span>
        </Link>
      </td>
      <td className="py-2.5 px-3 text-slate-700">{r.recipient?.full_name?.split(' ')[0] ?? '—'}</td>
      <td className="py-2.5 px-3 text-slate-600">{fmtDate(r.updated_at)}</td>
      <td className="py-2.5 px-3"><StatusPill status={r.status} /></td>
      <td className="py-2.5 px-3 text-right font-mono text-slate-900">{r.cart_subtotal_htg_minor > 0 ? formatHtgMinor(r.cart_subtotal_htg_minor, { withSymbol: false }) : <span className="text-slate-400">—</span>}</td>
      <td className="py-2.5 px-4 text-right">
        {r.status === 'confirmed' ? (
          <div className="inline-flex items-center gap-3">
            <Link to={`/customer/requests/new?reorder=${r.id}`} className="text-[11.5px] font-semibold text-brand-600 hover:underline inline-flex items-center gap-1"><RotateCcw className="w-3 h-3" /> Order again</Link>
            <ReceiptLink requestId={r.id} />
          </div>
        ) : <span className="text-slate-300 text-[11px]">—</span>}
      </td>
    </tr>
  );
}

export function ReceiptLink({ requestId }: { requestId: number }) {
  const [lang, setLang] = useState<CertLang>('en');
  const [busy, setBusy] = useState(false);
  const dl = async () => {
    setBusy(true);
    try { await receipts.download(requestId, lang); }
    catch (e) { toast.error((e as Error).message || 'Download failed'); }
    finally { setBusy(false); }
  };
  return (
    <span className="inline-flex items-center gap-1.5">
      <select value={lang} onChange={(e) => setLang(e.target.value as CertLang)} onClick={(e) => e.stopPropagation()} className="text-[10px] border border-slate-200 rounded px-1 py-0.5 bg-white" aria-label="Receipt language">
        <option value="en">EN</option><option value="fr">FR</option><option value="ht">HT</option>
      </select>
      <button onClick={dl} disabled={busy} className="inline-flex items-center gap-1 text-[11.5px] font-semibold text-emerald-700 hover:text-emerald-800">
        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />} Receipt
      </button>
    </span>
  );
}
